// src/pages/OKRs.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "components/ui/card";
import { PageHeader, StatGrid, Stat } from "components/ui/page-header";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Progress } from "components/ui/progress";
import { Plus, Target, TrendingUp, Calendar, Edit, Trash2, CheckCircle2 } from "lucide-react";
import CreateOKRDialog from "components/okrs/CreateOKRDialog";
import { Collapsible } from "components/ui/compact";
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import EditOKRDialog from "components/okrs/EditOKRDialog";
import { okrsService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import SampleBadge from "components/guide/SampleBadge";

export default function OKRs() {
  const { user, loading } = useAuth();
  const [okrs, setOkrs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingOKR, setEditingOKR] = useState(null);

  const loadOKRs = useCallback(async () => {
    if (loading || !user?.id) {
      if (!loading && !user?.id) {
        setOkrs([]);
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      console.log('Loading OKRs for user:', user.id);
      const data = await okrsService.list("-updated_at", 999);
      console.log('OKRs loaded:', data.length);
      setOkrs(data);
    } catch (error) {
      console.error('Error loading OKRs:', error);
      setOkrs([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, loading]);

  useEffect(() => {
    loadOKRs();
    window.addEventListener('spcc:autopilot-updated', loadOKRs);
    return () => window.removeEventListener('spcc:autopilot-updated', loadOKRs);
  }, [loadOKRs]);

  const handleCreate = async (okrData) => {
    try {
      console.log('Creating OKR:', okrData);
      await okrsService.create(okrData);
      setShowCreate(false);
      await loadOKRs();
      console.log('OKR created successfully');
    } catch (error) {
      console.error('Error creating OKR:', error);
      alert(`Failed to create OKR: ${error.message}`);
    }
  };

  const handleEdit = async (okrData) => {
    try {
      console.log('Updating OKR:', editingOKR.id, okrData);
      await okrsService.update(editingOKR.id, okrData);
      setEditingOKR(null);
      await loadOKRs();
      console.log('OKR updated successfully');
    } catch (error) {
      console.error('Error updating OKR:', error);
      alert(`Failed to update OKR: ${error.message}`);
    }
  };

  const handleDelete = async (okrId) => {
    if (!window.confirm('Are you sure you want to delete this OKR? This action cannot be undone.')) {
      return;
    }

    try {
      console.log('Deleting OKR:', okrId);
      await okrsService.delete(okrId);
      await loadOKRs();
      console.log('OKR deleted successfully');
    } catch (error) {
      console.error('Error deleting OKR:', error);
      alert(`Failed to delete OKR: ${error.message}`);
    }
  };

  const handleUpdateProgress = async (okrId, newProgress) => {
    try {
      await okrsService.update(okrId, { progress_percentage: newProgress });
      await loadOKRs();
    } catch (error) {
      console.error('Error updating progress:', error);
      alert(`Failed to update progress: ${error.message}`);
    }
  };

  const STATUS_TONE = { active: "success", paused: "warning", completed: "accent", draft: "default" };

  const getProgressColor = (progress) => {
    if (progress >= 70) return "var(--success)";
    if (progress >= 30) return "var(--fg)";
    return "var(--fg-muted)";
  };

  const getCurrentQuarter = () => {
    const now = new Date();
    const year = now.getFullYear();
    const quarter = Math.ceil((now.getMonth() + 1) / 3);
    return `${year}-Q${quarter}`;
  };

  const groupedOKRs = okrs.reduce((groups, okr) => {
    const quarter = okr.quarter || getCurrentQuarter();
    if (!groups[quarter]) groups[quarter] = [];
    groups[quarter].push(okr);
    return groups;
  }, {});

  const sortedQuarters = Object.keys(groupedOKRs).sort().reverse();

  if (isLoading) {
    return (
      <div className="loading-screen" style={{ height: 240 }} role="status">
        <div><div className="spinner" /><span>Loading OKRs…</span></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={Target}
        eyebrow="Plan"
        title="Objectives & Key Results"
        description="Define and track strategic objectives with measurable key results."
        actions={
          <Button variant="primary" onClick={() => setShowCreate(true)} data-guide="objective-add">
            <Plus size={15} />
            New OKR
          </Button>
        }
      />

      <StatGrid>
        <Stat label="Total OKRs" value={okrs.length} />
        <Stat label="Active" value={okrs.filter(o => o.status === 'active').length} />
        <Stat label="Completed" value={okrs.filter(o => o.progress_percentage >= 100).length} />
        <Stat label="Avg progress" value={`${Math.round(okrs.reduce((sum, o) => sum + (o.progress_percentage || 0), 0) / okrs.length) || 0}%`} />
      </StatGrid>
      <div className="v2-page"><WorkspaceInsights area="okrs" limit={6}/></div>

      {sortedQuarters.length === 0 ? (
        <Card>
          <div className="empty">
            <Target size={32} />
            <strong>No OKRs yet</strong>
            <span>Create your first OKR to start tracking objectives and key results.</span>
            <Button variant="primary" onClick={() => setShowCreate(true)} style={{ marginTop: 8 }}>
              <Plus size={15} />
              Create first OKR
            </Button>
          </div>
        </Card>
      ) : (
        // Newest quarter open, earlier quarters folded to one line each.
        sortedQuarters.map((quarter, quarterIndex) => (
          <Collapsible key={quarter} id={`okrs:${quarter}`} defaultOpen={quarterIndex === 0} className="bare" aria-label={`${quarter} objectives`}
            icon={Calendar} title={quarter} meta={`${groupedOKRs[quarter].length} OKR${groupedOKRs[quarter].length !== 1 ? 's' : ''}`}
            summary={`Avg progress ${Math.round(groupedOKRs[quarter].reduce((sum, o) => sum + (o.progress_percentage || 0), 0) / groupedOKRs[quarter].length)}%`}>
            <div className="grid cols-2">
              {groupedOKRs[quarter].map(okr => {
                const keyResults = Array.isArray(okr.key_results) ? okr.key_results : [];
                const progress = okr.progress_percentage || 0;

                return (
                  <Card key={okr.id} style={{ display: "grid", gap: 14, alignContent: "start" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                          <Badge variant={STATUS_TONE[okr.status] || "default"} className="badge-dot" style={{ textTransform: "capitalize" }}>
                            {okr.status || "draft"}
                          </Badge>
                          {progress >= 100 && (
                            <Badge variant="success"><CheckCircle2 size={11} />Completed</Badge>
                          )}
                        </div>
                        <h3 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>{okr.title}</h3>
                        <SampleBadge id={okr.id}/>
                        {okr.description && (
                          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--fg-muted)", lineHeight: 1.5 }}>
                            {okr.description}
                          </p>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 2 }}>
                        <Button variant="ghost" size="sm" className="btn-icon" onClick={() => setEditingOKR(okr)} title="Edit OKR" aria-label="Edit OKR">
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="btn-icon" onClick={() => handleDelete(okr.id)} title="Delete OKR" aria-label="Delete OKR" style={{ color: "var(--danger-fg)" }}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: "var(--fg-muted)" }}>Progress</span>
                        <span className="tabular" style={{ fontSize: 15, fontWeight: 600, color: getProgressColor(progress) }}>
                          {progress}%
                        </span>
                      </div>
                      <Progress value={progress} />
                      <div className="segmented" style={{ marginTop: 10 }} role="group" aria-label="Set progress">
                        {[0, 25, 50, 75, 100].map(value => (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={progress === value}
                            onClick={() => handleUpdateProgress(okr.id, value)}
                            className="tabular"
                            style={{ fontSize: 12, height: 24 }}
                          >
                            {value}%
                          </button>
                        ))}
                      </div>
                    </div>

                    {keyResults.length > 0 && (
                      <div>
                        <h4 className="meta" style={{ margin: "0 0 6px", fontWeight: 500 }}>
                          Key results · {keyResults.length}
                        </h4>
                        <KeyResultList items={keyResults.slice(0, 3)} />
                        {keyResults.length > 3 && (
                          <Collapsible className="plain okr-more" title={`${keyResults.length - 3} more key result${keyResults.length - 3 === 1 ? '' : 's'}`}>
                            <KeyResultList items={keyResults.slice(3)} />
                          </Collapsible>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </Collapsible>
        ))
      )}

      {/* Create Dialog */}
      <CreateOKRDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
      />

      {/* Edit Dialog */}
      {editingOKR && (
        <EditOKRDialog
          open={!!editingOKR}
          onClose={() => setEditingOKR(null)}
          onSubmit={handleEdit}
          okr={editingOKR}
        />
      )}
    </div>
  );
}

function KeyResultList({ items }) {
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, border: "1px solid var(--border)", borderRadius: "var(--radius-md)" }}>
      {items.map((kr, index) => (
        <li
          key={index}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", fontSize: 13, color: "var(--fg-secondary)", borderTop: index ? "1px solid var(--border)" : 0 }}
        >
          <TrendingUp size={13} color="var(--fg-muted)" />
          <span style={{ flex: 1, minWidth: 0 }}>{kr.title || kr.description || kr}</span>
          {typeof kr.progress === "number" && <span className="tabular" style={{ fontSize: 12, color: "var(--fg-muted)" }}>{kr.progress}%</span>}
        </li>
      ))}
    </ul>
  );
}
